import { useState, Dispatch, SetStateAction, FormEvent, useEffect } from 'react'
import {
  Box,
  Text,
  RadioGroup,
  RadioItemBlock,
  Bleed,
  Badge,
  Skeleton,
  Stack
} from '@machobear/design-system'
import { propEq, reject, map, prop, pipe, filter, evolve, isEmpty, flatten, anyPass } from 'ramda'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import Fuse from 'fuse.js'
import { useFormikContext, FormikErrors } from 'formik'
import useMyListings from '@/pages/Listing/MyListings/hooks/use-my-listings'
import formatCurrency from '@/utils/format-currency'
import { ListingTownHouseIcon, CircleErrorRedNew } from '@/components/icons'
import SearchBar from './search-input'
import { textBox } from './content.css'

type Props = {
  excludeHidden?: boolean
  value: string
  onChange?: () => void
}
type fuseResult = {
  item: any
}

interface FormValues {
  listing: string
  listingData: any
}

const filterActiveListings = (listings: any, excludeHidden: boolean) => {
  return pipe(
    map(
      evolve({
        listings: pipe(
          filter(
            anyPass([propEq('state_machine', 'published'), propEq('state_machine', 'pending')])
          ),
          listing => (excludeHidden ? reject(propEq('is_hidden', '1'))(listing) : listing)
        )
      })
    ),
    reject((building: any) => isEmpty(building.listings)),
    map(prop('listings')),
    flatten
  )(listings)
}

const getListingLabel = (listing: {
  listing_id: string
  unit_number?: string
  address: string
}) => {
  let returnString = ''
  const unitText = `${listing.unit_number} - `
  if (listing?.unit_number) {
    returnString += unitText
  }
  return (returnString += `${listing?.address}`)
}
const StyledIcon = styled(ListingTownHouseIcon)`
  height: 40px;
  width: 40px;
`

const DialogContent = ({ excludeHidden = false, value }: Props) => {
  const { t } = useTranslation()
  const formik = useFormikContext()
  const { listings: allMyListings } = useMyListings()
  const activeListings = filterActiveListings(allMyListings || [], excludeHidden)
  const [listings, setListings] = useState<any[]>(activeListings)
  const [isLoading, setIsLoading] = useState(true)

  const {
    errors,
    setValues,
    touched
  }: {
    errors: FormikErrors<FormValues>
    setValues: Dispatch<SetStateAction<FormValues>>
    touched: { listing?: string }
  } = formik
  const hasListingError = touched?.listing && errors?.listing
  useEffect(() => {
    if (hasListingError) {
      window?.dataLayer?.push({
        event: 'application-trust-score-documents-submit-purchase-credit'
      })
    }
  }, [hasListingError])

  useEffect(() => {
    setIsLoading(false)
  }, [])

  const options = {
    keys: [
      {
        name: 'address',
        weight: 0.7
      },
      {
        name: 'city',
        weight: 0.7
      },
      {
        name: 'unit_number',
        weight: 0.7
      },
      {
        name: 'building_name',
        weight: 0.3
      },
      {
        name: 'state',
        weight: 0.3
      }
    ]
  }

  // Create a new instance of Fuse
  const fuse = new Fuse(activeListings, options)

  const handleSearch = (search: string) => {
    if (search.length === 0) {
      setListings(activeListings)
    } else {
      const searchResults = fuse.search(search)
      const results = pipe(map((x: fuseResult) => x.item))(searchResults)
      setListings(results)
    }
  }

  return (
    <>
      <Box display="flex" flexDirection="column" gap="small" style={{ paddingBottom: 'gutter' }}>
        <Box flexDirection="column" display="flex" gap="xxsmall">
          <Text size="medium" weight="regular">
            {t(
              'b.usage.dialog.to_get_started.title',
              "To get started, please select the listing you'd like to use this credit on."
            )}
          </Text>

          <SearchBar placeholder={t('common.search', 'search')} onSearch={handleSearch} fullWidth />

          {errors.listing && (
            <Box display="flex" gap="xxsmall" alignItems="center">
              <CircleErrorRedNew />
              <Text size="small" tone="critical">
                {t('b.usage.credits.error.text', 'Please select at least 1 listing to continue.')}
              </Text>
            </Box>
          )}
        </Box>
        {isLoading ? (
          <Box marginY="xsmall">
            <Stack space="small">
              <Skeleton.Shape height={80} width={'100%'} />
              <Skeleton.Shape height={80} width={'100%'} />
              <Skeleton.Shape height={80} width={'100%'} />
            </Stack>
          </Box>
        ) : (
          <>
            {isEmpty(listings) ? (
              <Box
                flexDirection="column"
                display="flex"
                gap="xxsmall"
                alignItems="center"
                paddingY="large"
                style={{ height: '45vh' }}
              >
                <Text size="medium" weight="medium">
                  {t('b.usage.dialog.no_listings_found.text', 'No listings found.')}
                </Text>
                <Text size="medium" weight="regular">
                  {t(
                    'b.usage.dialog.try_adjusting_your_search.text',
                    'Try adjusting your search or try again.'
                  )}
                </Text>
              </Box>
            ) : (
              <Bleed horizontal="xxxxsmall">
                <Box
                  paddingX="xxxxsmall"
                  paddingBottom="medium"
                  style={{ height: '45vh', overflowY: 'auto' }}
                >
                  <RadioGroup
                    id="listing"
                    label=""
                    value={value}
                    onChange={(e: FormEvent<HTMLInputElement>) => {
                      setValues({
                        listing: e.currentTarget.value,
                        listingData: listings.find(x => x.listing_id === e.currentTarget.value)
                      })
                    }}
                    size="small"
                  >
                    {listings.map((listing, index) => {
                      return (
                        <RadioItemBlock
                          label={getListingLabel(listing)}
                          value={listing?.listing_id}
                          icon={<StyledIcon />}
                          key={index}
                          badge={
                            listing.state_machine === 'pending' ? (
                              <Badge variant="solid" tone="caution">
                                {t('b.payment.view.payment_status_pending.text', 'Pending')}
                              </Badge>
                            ) : undefined
                          }
                        >
                          <Box className={textBox}>
                            <Text weight="medium" baseline={false} tone="secondary" size="small">
                              {listing?.city}, {listing?.state}
                              {listing?.price && (
                                <>
                                  {' '}
                                  ·{' '}
                                  {formatCurrency(listing?.price).includes('.00')
                                    ? formatCurrency(listing?.price).split('.')[0]
                                    : formatCurrency(listing?.price)}
                                  {` / ${t(
                                    `b.listing.view.price_frequency_${listing?.price_frequency}.text`
                                  )}`}
                                </>
                              )}
                            </Text>
                          </Box>
                        </RadioItemBlock>
                      )
                    })}
                  </RadioGroup>
                </Box>
              </Bleed>
            )}
          </>
        )}
      </Box>
    </>
  )
}

export default DialogContent
